/**
 * TraceabilityGraph - Visualizacion de Trazabilidad de Requisitos
 * PECAL 2210 - Trazabilidad Bidireccional
 */

import { useMemo, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ZoomIn, ZoomOut, Maximize2, RefreshCw, Download, GitBranch, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTraceabilityGraph } from '@/hooks/useRequirements';
import { cn } from '@/lib/utils';
import {
  TraceabilityGraphNode,
  TraceabilityGraphEdge,
  levelColors,
  levelLabels,
  statusColors,
  statusLabels,
  priorityColors,
} from '@/types/requirements';

interface TraceabilityGraphProps {
  rootRequirementId?: string | null;
  onNodeClick?: (nodeId: string) => void;
  className?: string;
}

const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;
const LEVEL_GAP = 150;
const NODE_GAP = 80;

const LEVEL_ORDER = ['str', 'sys', 'srs', 'des', 'tst'];

export function TraceabilityGraph({
  rootRequirementId,
  onNodeClick,
  className,
}: TraceabilityGraphProps) {
  const [depth, setDepth] = useState(3);
  const [zoom, setZoom] = useState(1);
  const [colorBy, setColorBy] = useState<'level' | 'status' | 'priority'>('level');

  const { data: graph, isLoading, refetch } = useTraceabilityGraph(rootRequirementId, depth);

  const { nodes, edges, positions, svgWidth, svgHeight } = useMemo(() => {
    if (!graph?.nodes?.length) {
      return { nodes: [], edges: [], positions: new Map(), svgWidth: 800, svgHeight: 400 };
    }

    const nodesByLevel = new Map<string, TraceabilityGraphNode[]>();
    for (const node of graph.nodes) {
      const level = node.level || 'sys';
      if (!nodesByLevel.has(level)) {
        nodesByLevel.set(level, []);
      }
      nodesByLevel.get(level)!.push(node);
    }

    const positions = new Map<string, { x: number; y: number }>();
    let maxY = 0;

    LEVEL_ORDER.forEach((level, levelIndex) => {
      const levelNodes = nodesByLevel.get(level) || [];
      const x = 50 + levelIndex * LEVEL_GAP;
      levelNodes.forEach((node, nodeIndex) => {
        const y = 50 + nodeIndex * NODE_GAP;
        positions.set(node.id, { x, y });
        if (y > maxY) maxY = y;
      });
    });

    const svgWidth = 50 + LEVEL_ORDER.length * LEVEL_GAP + NODE_WIDTH;
    const svgHeight = maxY + NODE_HEIGHT + 50;

    return {
      nodes: graph.nodes,
      edges: graph.edges || [],
      positions,
      svgWidth,
      svgHeight,
    };
  }, [graph]);

  const getNodeColor = useCallback(
    (node: TraceabilityGraphNode) => {
      switch (colorBy) {
        case 'level':
          return levelColors[node.level] || 'bg-gray-100 text-gray-800';
        case 'status':
          return statusColors[node.status] || 'bg-gray-100 text-gray-800';
        case 'priority':
          return priorityColors[node.priority] || 'bg-gray-100 text-gray-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },
    [colorBy]
  );

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.4));
  const handleResetZoom = () => setZoom(1);

  const handleExport = () => {
    const svg = document.getElementById('traceability-svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traceability-${rootRequirementId || 'all'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="w-5 h-5" />
            Grafo de Trazabilidad
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={String(depth)} onValueChange={(v) => setDepth(Number(v))}>
              <SelectTrigger className="w-28">
                <SelectValue placeholder="Profundidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 nivel</SelectItem>
                <SelectItem value="2">2 niveles</SelectItem>
                <SelectItem value="3">3 niveles</SelectItem>
                <SelectItem value="5">5 niveles</SelectItem>
              </SelectContent>
            </Select>

            <Select value={colorBy} onValueChange={(v: any) => setColorBy(v)}>
              <SelectTrigger className="w-28">
                <SelectValue placeholder="Color por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="level">Nivel</SelectItem>
                <SelectItem value="status">Estado</SelectItem>
                <SelectItem value="priority">Prioridad</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center border rounded-md">
              <Button variant="ghost" size="icon" onClick={handleZoomOut}>
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="px-2 text-sm">{Math.round(zoom * 100)}%</span>
              <Button variant="ghost" size="icon" onClick={handleZoomIn}>
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleResetZoom}>
                <Maximize2 className="w-4 h-4" />
              </Button>
            </div>

            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleExport}>
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {nodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>No hay requisitos para mostrar</p>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-4">
              {LEVEL_ORDER.map((level) => (
                <Badge key={level} className={cn(levelColors[level], 'text-xs')}>
                  {levelLabels[level]}
                </Badge>
              ))}
            </div>

            <div
              className="overflow-auto border rounded-lg bg-slate-50"
              style={{ maxHeight: 500 }}
            >
              <svg
                id="traceability-svg"
                width={svgWidth * zoom}
                height={svgHeight * zoom}
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                className="min-w-full"
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
                  </marker>
                </defs>

                {edges.map((edge, idx) => {
                  const sourcePos = positions.get(edge.source);
                  const targetPos = positions.get(edge.target);
                  if (!sourcePos || !targetPos) return null;

                  const x1 = sourcePos.x + NODE_WIDTH;
                  const y1 = sourcePos.y + NODE_HEIGHT / 2;
                  const x2 = targetPos.x;
                  const y2 = targetPos.y + NODE_HEIGHT / 2;

                  const midX = (x1 + x2) / 2;
                  const path = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;

                  return (
                    <g key={`edge-${idx}`}>
                      <path
                        d={path}
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="1.5"
                        markerEnd="url(#arrowhead)"
                      />
                      {edge.label && (
                        <text
                          x={midX}
                          y={(y1 + y2) / 2 - 5}
                          textAnchor="middle"
                          className="text-[10px] fill-slate-500"
                        >
                          {edge.label}
                        </text>
                      )}
                    </g>
                  );
                })}

                {nodes.map((node) => {
                  const pos = positions.get(node.id);
                  if (!pos) return null;

                  const isVerified = node.verification_status === 'passed';

                  return (
                    <Tooltip key={node.id}>
                      <TooltipTrigger asChild>
                        <g
                          transform={`translate(${pos.x}, ${pos.y})`}
                          onClick={() => onNodeClick?.(node.id)}
                          className="cursor-pointer"
                        >
                          <rect
                            width={NODE_WIDTH}
                            height={NODE_HEIGHT}
                            rx="6"
                            className={cn(
                              'fill-white stroke-slate-300 hover:stroke-primary transition-colors',
                              onNodeClick && 'hover:fill-slate-50'
                            )}
                            strokeWidth="1.5"
                          />
                          <foreignObject x="4" y="4" width={NODE_WIDTH - 8} height={NODE_HEIGHT - 8}>
                            <div className="flex flex-col h-full justify-between">
                              <span className="text-xs font-semibold text-slate-700 truncate">
                                {node.label}
                              </span>
                              <span className="text-[10px] text-slate-500 truncate">
                                {node.title}
                              </span>
                              <div className="flex items-center gap-1">
                                <span
                                  className={cn(
                                    'text-[9px] px-1.5 py-0.5 rounded',
                                    getNodeColor(node)
                                  )}
                                >
                                  {colorBy === 'level'
                                    ? levelLabels[node.level]
                                    : colorBy === 'status'
                                    ? statusLabels[node.status]
                                    : node.priority}
                                </span>
                                {isVerified && (
                                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                                )}
                              </div>
                            </div>
                          </foreignObject>
                        </g>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-xs">
                          <p className="font-semibold">{node.label}</p>
                          <p>{node.title}</p>
                          <p className="text-muted-foreground mt-1">
                            {levelLabels[node.level]} | {statusLabels[node.status]}
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </svg>
            </div>

            <div className="flex justify-between mt-3 text-xs text-muted-foreground">
              <span>{nodes.length} requisitos</span>
              <span>{edges.length} enlaces</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
